import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DogsContext = createContext(null);

export function DogsProvider({ children }) {
  const { user } = useAuth();
  const [dogs, setDogs] = useState([]);
  const [activeDogId, setActiveDogId] = useState(() => localStorage.getItem("activeDogId") || null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/dogs");
      const list = data.dogs || [];
      setDogs(list);
      setActiveDogId((current) => {
        if (current && list.find((d) => d.id === current)) return current;
        const first = list[0];
        if (first) {
          localStorage.setItem("activeDogId", first.id);
          return first.id;
        }
        localStorage.removeItem("activeDogId");
        return null;
      });
    } catch (err) {
      // 401s here happen during the brief window between logout and the
      // AuthContext finishing its redirect — never surface as runtime errors.
      if (err?.response?.status !== 401) {
        console.warn("Dogs refresh failed:", err?.response?.status, err?.message);
      }
      setDogs([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) refresh();
  }, [user, refresh]);

  const selectDog = useCallback((id) => {
    setActiveDogId(id);
    if (id) localStorage.setItem("activeDogId", id);
  }, []);

  const activeDog = useMemo(
    () => dogs.find((d) => d.id === activeDogId) || null,
    [dogs, activeDogId],
  );

  const value = useMemo(
    () => ({ dogs, activeDog, activeDogId, selectDog, refresh, loading }),
    [dogs, activeDog, activeDogId, selectDog, refresh, loading],
  );

  return <DogsContext.Provider value={value}>{children}</DogsContext.Provider>;
}

export const useDogs = () => useContext(DogsContext);
